import Image from 'next/image';
import { Content, isFilled } from '@prismicio/client';
import { PrismicNextLink } from '@prismicio/next';
import { PrismicRichText } from '@prismicio/react';
import { ArrowRightIcon } from '@/assets';
import { MotionSafeVideo } from '@/components/common/MotionSafeVideo';
import { useMemo } from 'react';
import { useWorkableJobs } from '@/hooks';
import { toJobCardModel } from '@/utils/workable';

type WithPillLabelAndJobCardSlice = Extract<
  Content.HeroCtaWithLogosSlice,
  { variation: 'withPillLabelAndJobCard' }
>;

interface WithPillLabelAndJobCardProps {
  slice: WithPillLabelAndJobCardSlice;
  openModal: () => void;
}

const LocationIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    width="16"
    height="16"
    aria-hidden="true"
  >
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
  </svg>
);

const ClockIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    width="16"
    height="16"
    aria-hidden="true"
  >
    <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8zm1-13h-2v6l4.243 4.243 1.414-1.414L13 11.586V7z" />
  </svg>
);

const JobCardSkeleton = () => (
  <div className="backdrop-blur-[10px] bg-black/40 flex flex-col p-6 rounded-[40px] shrink-0 w-[303px] animate-pulse">
    <div className="flex flex-col gap-6 h-full">
      <div className="flex flex-col gap-2 flex-1">
        <div className="h-5 bg-white/20 rounded w-3/4" />
        <div className="flex gap-6 items-center mt-1">
          <div className="h-4 bg-white/20 rounded w-1/3" />
          <div className="h-4 bg-white/20 rounded w-1/3" />
        </div>
      </div>
      <div className="h-12 bg-white/20 rounded-full w-full" />
    </div>
  </div>
);

const WithPillLabelAndJobCard = ({
  slice,
  openModal,
}: WithPillLabelAndJobCardProps) => {
  const { title, description, buttons, pill, bg_video } = slice.primary;
  const prismicJobs = slice.items;
  const videoUrl = isFilled.linkToMedia(bg_video) ? bg_video.url : undefined;
  const jobBgImages = prismicJobs
    .map((item) => (isFilled.image(item.job_image) ? item.job_image.url : null))
    .filter((url): url is string => url !== null);

  const { jobs: workableJobs, isLoading } = useWorkableJobs();
  const cards = useMemo(
    () => workableJobs?.map(toJobCardModel) ?? [],
    [workableJobs],
  );

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative text-white overflow-hidden min-h-screen landscape-short:min-h-0 flex items-center"
    >
      {/* Background */}
      {videoUrl ? (
        <MotionSafeVideo
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          src={videoUrl}
        />
      ) : (
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, #0E1010 0%, #0F423A 100%)',
          }}
        />
      )}
      <div className="absolute inset-0 bg-black/40" />

      <div className="relative z-10 w-full container mx-auto px-4 md:px-[150px] py-[135px] landscape-short:py-16 flex flex-col gap-[30px]">
        {/* Content card */}
        <div className="backdrop-blur-[10px] bg-black/20 flex flex-col gap-4 items-center p-10 rounded-[40px]">
          {/* Pill label */}
          {pill && (
            <div className="border border-[#00F557] px-4 py-2 rounded-full shrink-0">
              <span className="text-white text-[14px] font-normal uppercase leading-[1.2] tracking-wide">
                {pill}
              </span>
            </div>
          )}

          {/* Title */}
          <PrismicRichText
            field={title}
            components={{
              heading1: ({ children }) => (
                <h1 className="text-white font-semibold text-4xl md:text-[56px] leading-[1.2] text-center w-full">
                  {children}
                </h1>
              ),
            }}
          />

          {/* Description */}
          <div className="text-white text-[18px] leading-[1.4] text-center max-w-3xl">
            <PrismicRichText field={description} />
          </div>

          {/* Buttons */}
          {buttons && buttons.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
              {buttons
                .filter((item) => item.button_text)
                .map((item, index) => {
                  const classes =
                    index === 0 ? 'primary-button' : 'tertiary-button';

                  if (item.open_modal) {
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          openModal();
                        }}
                        className={classes}
                      >
                        <ArrowRightIcon className="arrow-icon" />
                        {item.button_text}
                      </button>
                    );
                  }

                  return (
                    <PrismicNextLink
                      key={index}
                      field={item.button_link}
                      className={classes}
                    >
                      <ArrowRightIcon className="arrow-icon" />
                      {item.button_text}
                    </PrismicNextLink>
                  );
                })}
            </div>
          )}
        </div>

        {/* Job cards row */}
        {(() => {
          const skeletonCount =
            prismicJobs?.length > 0 ? prismicJobs.length : 3;

          if (isLoading) {
            return (
              <div
                className="relative overflow-hidden"
                style={{
                  WebkitMaskImage:
                    'linear-gradient(to right, black 0%, black calc(100% - 120px), transparent 100%)',
                  maskImage:
                    'linear-gradient(to right, black 0%, black calc(100% - 120px), transparent 100%)',
                }}
              >
                <div className="flex gap-6 items-stretch overflow-x-auto no-scrollbar pr-24">
                  {Array.from({ length: skeletonCount }).map((_, i) => (
                    <JobCardSkeleton key={i} />
                  ))}
                </div>
              </div>
            );
          }

          if (cards.length === 0) {
            return null;
          }

          return (
            <div
              className="relative overflow-hidden"
              style={{
                WebkitMaskImage:
                  'linear-gradient(to right, black 0%, black calc(100% - 120px), transparent 100%)',
                maskImage:
                  'linear-gradient(to right, black 0%, black calc(100% - 120px), transparent 100%)',
              }}
            >
              <div className="flex gap-6 items-stretch overflow-x-auto no-scrollbar pr-24">
                {cards.map((card, index) => {
                  const cardBgUrl =
                    jobBgImages.length > 0
                      ? jobBgImages[index % jobBgImages.length]
                      : undefined;
                  return (
                    <div
                      key={card.key}
                      className="backdrop-blur-[10px] bg-black/40 flex flex-col gap-6 p-6 rounded-[40px] shrink-0 w-[303px]"
                    >
                      {cardBgUrl && (
                        <div className="h-[63px] relative overflow-hidden rounded-2xl shrink-0 w-full">
                          <div
                            className="absolute left-0 w-full"
                            style={{ height: '238%', top: '-33.07%' }}
                          >
                            <Image
                              src={cardBgUrl}
                              alt=""
                              fill
                              sizes="303px"
                              className="object-cover"
                            />
                          </div>
                        </div>
                      )}
                      <div className="flex flex-col gap-2 flex-1">
                        <p className="font-bold text-[16px] text-white leading-[1.5]">
                          {card.title}
                        </p>
                        <div className="flex gap-6 items-center">
                          {card.location && (
                            <div className="flex flex-1 gap-1 items-center text-[#EEEEEE] min-w-0">
                              <LocationIcon />
                              <span className="text-[14px] font-medium leading-[1.5] whitespace-nowrap">
                                {card.location}
                              </span>
                            </div>
                          )}
                          {card.type && (
                            <div className="flex flex-1 gap-1 items-center text-[#EEEEEE] min-w-0">
                              <ClockIcon />
                              <span className="text-[14px] font-medium leading-[1.5] whitespace-nowrap">
                                {card.type}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <a
                        href={card.applyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="border border-white text-white font-bold text-[16px] leading-[1.5] px-6 py-3 rounded-full text-center w-full transition-all duration-300 hover:bg-gradient-to-r hover:from-[#00F2A5] hover:to-[#00C2FF] hover:shadow-[0_0_15px_rgba(0,242,165,0.8)] hover:border-transparent"
                      >
                        Apply
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
    </section>
  );
};

export default WithPillLabelAndJobCard;
